import { ReactElement, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  NumberInput,
  NumberInputField,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { TextInput } from "../../atoms/AppInputs";
import { SimpleAlertDialog } from "../../atoms/SimpleAlertDialog";
import { useModifiedTranslation } from "../../../hooks/useModifiedTranslation";
import { PhraseListField } from "./PhraseListField";
import { PhraseSettingLayout } from "./PhraseSettingLayout";
import {
  isInvalidPhrase,
  PhrasesSettingFormItem,
  RESERVED_SETTING_NAME,
  SettingErrors,
  validateSetting,
} from "./phraseSettingForm";

/*
 * 読み上げ文の設定（1件分の編集）
 */
export function PhraseSettingEditor(
  props: Readonly<{
    initialValue: PhrasesSettingFormItem;
    // APIから取得したこの設定の値。新規のときは無い
    savedValue: PhrasesSettingFormItem | undefined;
    minimumUtterances: number;
    isNew: boolean;
    onSave: (value: PhrasesSettingFormItem) => Promise<void>;
    onDelete: () => Promise<void>;
  }>,
): ReactElement {
  const {
    initialValue,
    savedValue,
    minimumUtterances,
    isNew,
    onSave,
    onDelete,
  } = props;
  const t = useModifiedTranslation();
  const [errors, setErrors] = useState<SettingErrors>({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    setValue,
    setFocus,
    getValues,
    formState: { isSubmitting },
  } = useForm<PhrasesSettingFormItem>({ defaultValues: initialValue });
  const settingName = useWatch({ control, name: "setting_name" });
  // 予約名は保存を待たず、入力した時点でエラーにする
  const settingNameError =
    settingName === RESERVED_SETTING_NAME
      ? "PhraseSetting.error.reservedSettingName"
      : errors.setting_name;

  // 画面の上にあるエラーから1つだけフォーカスする。フォーカスすると入力欄が見える位置までスクロールされる
  const focusFirstError = (
    value: PhrasesSettingFormItem,
    validationErrors: SettingErrors,
  ): void => {
    if (validationErrors.setting_name) {
      setFocus("setting_name");
    } else if (validationErrors.time) {
      setFocus("start_time");
    } else if (validationErrors.num_utterance) {
      setFocus("num_utterance");
    } else if (validationErrors.phrases) {
      // 原因の文が無いエラーは、保存ボタンのすぐ上の文言で見えている
      const index = value.phrases.findIndex((p) =>
        isInvalidPhrase(
          validationErrors.phrases,
          p,
          Number(value.num_utterance),
        ),
      );
      if (index !== -1) setFocus(`phrases.${index}.phrase`);
    }
  };

  const onSubmit = async (value: PhrasesSettingFormItem): Promise<void> => {
    const validationErrors = validateSetting(value, minimumUtterances);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      focusFirstError(value, validationErrors);
      return;
    }
    await onSave(value);
  };

  const handleDelete = async (): Promise<void> => {
    setIsConfirmingDelete(false);
    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <PhraseSettingLayout
      title={t("PhraseSetting.editTitle")}
      hasUnsavedChanges={() =>
        JSON.stringify(getValues()) !== JSON.stringify(savedValue)
      }
    >
      <VStack
        as="form"
        // ブラウザの入力チェックで送信を止めず、validateSetting の文言とスクロールで扱う
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        align="stretch"
        spacing={5}
        color="common_text.dark"
      >
        <FormControl isInvalid={!!settingNameError}>
          <FormLabel fontWeight="bold">
            {t("PhraseSetting.settingName")}
          </FormLabel>
          <TextInput
            isRequired={false}
            bg="common.base"
            {...register("setting_name")}
          />
          <FormErrorMessage>
            {settingNameError && t(settingNameError)}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.time}>
          <FormLabel fontWeight="bold">
            {t("PhraseSetting.timeRange")}
          </FormLabel>
          <Flex align="center" gap={2}>
            <TextInput
              isRequired={false}
              type="time"
              bg="common.base"
              aria-label={t("PhraseSetting.startTime")}
              {...register("start_time")}
            />
            <Text>〜</Text>
            <TextInput
              isRequired={false}
              type="time"
              bg="common.base"
              aria-label={t("PhraseSetting.endTime")}
              {...register("end_time")}
            />
          </Flex>
          <FormErrorMessage>{errors.time && t(errors.time)}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.num_utterance}>
          <FormLabel fontWeight="bold">
            {t("PhraseSetting.numUtterance")}
          </FormLabel>
          <Controller
            control={control}
            name="num_utterance"
            render={({ field }) => (
              <NumberInput
                min={minimumUtterances}
                step={1}
                precision={0}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              >
                <NumberInputField
                  ref={field.ref}
                  type="number"
                  min={minimumUtterances}
                  bg="common.base"
                />
              </NumberInput>
            )}
          />
          <FormErrorMessage>
            {errors.num_utterance && t(errors.num_utterance)}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.phrases}>
          <FormLabel fontWeight="bold">
            {t("PhraseSetting.phraseList")}
          </FormLabel>
          <PhraseListField
            control={control}
            register={register}
            setValue={setValue}
            errorKey={errors.phrases}
          />
          <FormErrorMessage>
            {errors.phrases && t(errors.phrases)}
          </FormErrorMessage>
        </FormControl>

        <Button
          data-testid="phrase-setting-save"
          type="submit"
          variant="btn_primary"
          w="full"
          size="lg"
          isLoading={isSubmitting}
          isDisabled={isDeleting}
        >
          {t("PhraseSetting.save")}
        </Button>
        {!isNew && (
          <Button
            data-testid="phrase-setting-delete"
            variant="ghost"
            colorScheme="red"
            isLoading={isDeleting}
            isDisabled={isSubmitting}
            onClick={() => setIsConfirmingDelete(true)}
          >
            {t("PhraseSetting.deleteSetting")}
          </Button>
        )}
        <SimpleAlertDialog
          isOpen={isConfirmingDelete}
          okTestId="phrase-setting-delete-confirm"
          ok={t("Modal.yes")}
          cancel={t("Modal.no")}
          description={t("PhraseSetting.confirmDelete")}
          onOk={handleDelete}
          onClose={() => setIsConfirmingDelete(false)}
        />
      </VStack>
    </PhraseSettingLayout>
  );
}

/*
 * 最小発話数の設定
 */
export function MinimumUtterancesEditor(
  props: Readonly<{
    initialValue: string;
    // APIから取得した最小発話数
    savedValue: string;
    numUtterances: number[];
    onSave: (minimumUtterances: string) => Promise<void>;
  }>,
): ReactElement {
  const { initialValue, savedValue, numUtterances, onSave } = props;
  const t = useModifiedTranslation();
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting },
  } = useForm<{ minimum_utterances: string }>({
    defaultValues: { minimum_utterances: initialValue },
  });

  const onSubmit = async (value: {
    minimum_utterances: string;
  }): Promise<void> => {
    const minimumUtterances = Number(value.minimum_utterances);
    if (
      value.minimum_utterances === "" ||
      numUtterances.some((n) => n < minimumUtterances)
    ) {
      setErrorKey("PhraseSetting.error.numUtteranceBelowMinimum");
      return;
    }
    setErrorKey(null);
    await onSave(value.minimum_utterances);
  };

  return (
    <PhraseSettingLayout
      title={t("PhraseSetting.minimumUtterances")}
      hasUnsavedChanges={() => getValues("minimum_utterances") !== savedValue}
    >
      <VStack
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        align="stretch"
        spacing={5}
        color="common_text.dark"
      >
        <FormControl isInvalid={!!errorKey}>
          <FormLabel fontWeight="bold">
            {t("PhraseSetting.minimumUtterances")}
          </FormLabel>
          <Controller
            control={control}
            name="minimum_utterances"
            render={({ field }) => (
              <NumberInput
                min={2}
                step={1}
                precision={0}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              >
                <NumberInputField
                  ref={field.ref}
                  type="number"
                  min={2}
                  bg="common.base"
                />
              </NumberInput>
            )}
          />
          <FormErrorMessage>{errorKey && t(errorKey)}</FormErrorMessage>
        </FormControl>
        <Button
          data-testid="phrase-setting-save"
          type="submit"
          variant="btn_primary"
          w="full"
          size="lg"
          isLoading={isSubmitting}
        >
          {t("PhraseSetting.save")}
        </Button>
      </VStack>
    </PhraseSettingLayout>
  );
}
